import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Row, Select } from "antd";

const { TextArea } = Input;
import { DeleteOutlined, DeleteFilled } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { OPENING_INVENTORY } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: OPENING_INVENTORY,
  BranchId: null,
  DepartmentId: null,
  Gross: 0,
  Tax: 0,
  Discount: 0,
  Amount: 0,
  VendorId: null,
  DemandMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailRate, setAddDetailRate] = useState("");
  const itemSelectRef = useRef(null);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  const fetchData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: OPENING_INVENTORY,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };


    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      // Update pagination
      const totalCount =
        dataSet?.TotalCount ||
        dataSet?.TotalRecords ||
        dataSet?.Data?.length ||
        0;

      // Set supporting tables if available
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || dataSet.Data || [],
        });
      }


    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };


  const handleFormChange = (data) => {
    setFormFields((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const rate = clampQtyRateValue(addDetailRate);

    if (selectedItemForAdd) {

      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: rate,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: true,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailRate("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyIU" || field === "RateIU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    if (field === "QtyIU" || field === "RateIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
      updatedDetails[index].RateIU = updatedDetails[index].RateIU || 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const calculateTotals = () => {
    let gross = 0;
    transactionDetails.forEach((detail) => {
      const lineAmount =
        (detail.QtyIU || 0) * (detail.RateIU || 0);
      gross += lineAmount;
    });

    const tax = 0;
    const discount = 0;
    const totalAmount = gross + tax - discount;

    setFormFields((prev) => ({
      ...prev,
      // Gross: gross,
      // Tax: tax,
      Amount: totalAmount,
      TotalAmount: totalAmount,
    }));
  };
  useEffect(() => {
    calculateTotals();
  }, [transactionDetails, formFields.Tax, formFields.Discount]);

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "OpeningInventory")) {
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId, // 2 for create, 3 for update
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: OPENING_INVENTORY,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: formFields.Gross || 0,
      Tax: formFields.Tax || 0,
      Discount: formFields.Discount || 0,
      Amount: formFields.Amount || 0,
      VendorId: formFields.VendorId || null,
      DemandMasterId: formFields.DemandMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: detail.RateIU || 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/openingInventory");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} opening inventory`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.QtyIU}
            onChange={(e) =>
              handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "RateIU",
        key: "RateIU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.RateIU}
            onChange={(e) =>
              handleDetailChange(index, "RateIU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Amount",
        key: "Amount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = record.QtyIU ?? record.QtyPU ?? 0;
          const rate = record.RateIU ?? record.RatePU ?? 0;
          const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
          return amount.toFixed(2);
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              >

              </Button>
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const formPanel = (
    <Fragment>
      <FormTextField
        colSpan={4}
        label="Transaction No"
        name="TransactionNo"
        size={INPUT_SIZE}
        value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
        readOnly={true}
      />

      <Col span={4}>
        <label style={{ display: "block", marginBottom: 5 }}>
          Transaction Date <span style={{ color: "red" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <>
            <DatePicker
              style={{ width: "100%" }}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </>
        ) : (
          <>
            <FormTextField
              name="TransactionDate"
              size={INPUT_SIZE}
              value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
              readOnly={true}
            />
          </>)}
      </Col>

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Branches || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={formFields.BranchId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <FormSelect
        colSpan={4}
        listItem={supportingTable.Departments || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={formFields.DepartmentId}
        onChange={handleFormChange}
        required={true}
        disabled={formFields.IsSubmit}
      />

      <Col span={12}>
        <label style={{ display: "block", marginBottom: 5 }}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={3}
          placeholder="Enter description"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>

      <FormTextField
        colSpan={4}
        label="Total Amount"
        name="TotalAmount"
        size={INPUT_SIZE}
        value={formFields.TotalAmount}
        type="text"
        isNumber="true"
        onChange={handleFormChange}
        readOnly={true}
      />
      <FormTextField
        colSpan={4}
        label="Total Items"
        name="TotalItems"
        size={INPUT_SIZE}
        value={transactionDetails.length}
        readOnly={true}
        type="text"
        isNumber="true"
      />

    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName + " ( " + item?.IssuanceUnit + " ) " ?? item?.Name ?? "";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (

        <Row gutter={16} style={{ flex: 1, minWidth: 0 }}>
          <Col flex="160px">
            <label style={{ display: "block", marginBottom: 4 }}>Bar Code</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Search by Bar Code"
              showSearch
              allowClear
              value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getBarcode(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList
                  .filter((i) => getBarcode(i))
                  .map((item) => ({
                    value: getBarcode(item),
                    label: getBarcode(item),
                  })),
              ]}
            />
          </Col>
          <Col flex="160px">
            <label style={{ display: "block", marginBottom: 4 }}>Item Code</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Search by Item Code"
              showSearch
              allowClear
              value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getItemCode(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList
                  .filter((i) => getItemCode(i))
                  .map((item) => ({
                    value: getItemCode(item),
                    label: getItemCode(item),
                  })),
              ]}
            />
          </Col>
          <Col flex="200px">
            <label style={{ display: "block", marginBottom: 4 }}>Item</label>
            <Select
              ref={itemSelectRef}
              style={{ width: "100%" }}
              placeholder="Search by Item Name"
              showSearch
              allowClear
              value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
              onChange={(value) => {
                const item = itemList.find((i) => getItemId(i) === value);
                setSelectedItemForAdd(item || null);
              }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={[
                ...itemList.map((item) => ({
                  value: getItemId(item),
                  label: getItemName(item),
                })),
              ]}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Qty</label>
            <Input
              type="number"
              placeholder="Qty"
              value={addDetailQty}
              onChange={(e) => setAddDetailQty(e.target.value)}
              min={0}
              step={1}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Rate</label>
            <Input
              type="number"
              placeholder="Rate"
              value={addDetailRate}
              onChange={(e) => setAddDetailRate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddDetail();
                }
              }}
              min={0}
              step={1}
            />
          </Col>
          <Col flex="120px">
            <label style={{ display: "block", marginBottom: 4 }}>Amount</label>
            <Input
              type="number"
              placeholder="Amount"
              value={addDetailRate}
              onChange={(e) => setAddDetailRate(e.target.value)}
              min={0}
              step={1}
              disabled={true}
            />
          </Col>
        </Row>

      )
      }
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Opening Inventory" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Opening Inventory" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/openingInventory"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "OpeningInventory", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/openingInventory")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
